import logo from "assets/images/integrations/rapidmail_logo.png";
import { Map } from "immutable";
import { useCallback, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootReduxState } from "../../../../../utils/types";
import { ConnectedIntegrationLineWrapper } from "../../components/ConnectedIntegrationLineWrapper";
import { deleteIntegrationAction, saveIntegrationAction } from "../../redux/shopData/actions";
import { IntegrationType } from "../../types";
import { RapidmailSettingsForm } from "./SettingsForm";
import { RapidmailIntegrationSettings, RapidmailListsSettings } from "./types";

const mapStateToProps = (state: RootReduxState) => {
  const selectedShop = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShop"
  ]) as string;

  const integrationList = state
    .getIn([
      "customerBackendDashboard",
      "shops",
      selectedShop,
      "integrations",
      "data",
      IntegrationType.RAPIDMAIL
    ])
    ?.toJS() as RapidmailIntegrationSettings[] | undefined;

  const connectedIntegration = integrationList?.find((integration) => {
    return integration.auth && integration.auth.username;
  });

  const isDeleting = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "deletingItem",
        IntegrationType.RAPIDMAIL,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  const isSaving = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "savingItem",
        IntegrationType.RAPIDMAIL,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  return {
    selectedShop,
    connectedIntegration,
    isDeleting,
    isSaving
  };
};

const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      deleteIntegrationAction,
      saveIntegrationAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type RapidmailSettingsViewComponentProps = ConnectedProps<typeof connector>;

const RapidmailSettingsViewComponent = (props: RapidmailSettingsViewComponentProps) => {
  const {
    connectedIntegration,
    isDeleting,
    selectedShop,
    deleteIntegrationAction,
    isSaving,
    saveIntegrationAction
  } = props;

  const handleDelete = useCallback(() => {
    if (connectedIntegration) {
      deleteIntegrationAction(selectedShop, IntegrationType.RAPIDMAIL, connectedIntegration.id);
    }
  }, [connectedIntegration, selectedShop]);

  const handleSave = useCallback(
    (values: RapidmailListsSettings) => {
      const duplicateApp = { ...connectedIntegration };
      Object.assign(duplicateApp, { settings: values });

      saveIntegrationAction(selectedShop, IntegrationType.RAPIDMAIL, duplicateApp);
    },
    [connectedIntegration, selectedShop]
  );

  const listOptions = useMemo(
    () => connectedIntegration?.info?.lists || [],
    [connectedIntegration?.info?.lists]
  );

  const initialValues = useMemo(
    () => Map(connectedIntegration?.settings || {}),
    [connectedIntegration?.settings]
  );

  if (!connectedIntegration) {
    return null;
  }

  return (
    <ConnectedIntegrationLineWrapper
      accountName={connectedIntegration.info?.accountName}
      deleting={isDeleting}
      image={logo}
      name="rapidmail"
      onDelete={handleDelete}
    >
      <RapidmailSettingsForm
        listOptions={listOptions}
        initialValues={initialValues}
        saving={isSaving}
        handleSave={handleSave}
      />
    </ConnectedIntegrationLineWrapper>
  );
};

export const RapidmailSettingsView = connector(RapidmailSettingsViewComponent);
